import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FileWithPreview extends File {
  preview?: string;
  id: string;
  type: 'image' | 'video';
  error?: string;
}

interface MultiFileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  disabled?: boolean;
}

const MultiFileUpload: React.FC<MultiFileUploadProps> = ({
  onFilesChange,
  maxFiles = 10,
  maxFileSize = 50,
  acceptedTypes = ['image/*', 'video/*'],
  disabled = false,
}) => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxFileSize) {
      return `فایل خیلی بزرگ است (حداکثر ${maxFileSize} مگابایت)`;
    }

    // Check file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      return 'نوع فایل پشتیبانی نمی‌شود';
    }

    // Check specific image formats
    if (isImage) {
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedImageTypes.includes(file.type)) {
        return 'فرمت تصویر پشتیبانی نمی‌شود';
      }
    }

    // Check specific video formats
    if (isVideo) {
      const allowedVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/webm'];
      if (!allowedVideoTypes.includes(file.type)) {
        return 'فرمت ویدیو پشتیبانی نمی‌شود';
      }
    }

    return null;
  };

  const createFilePreview = (file: File): Promise<FileWithPreview> => {
    return new Promise((resolve) => {
      const fileWithPreview: FileWithPreview = {
        ...file,
        id: Math.random().toString(36).substr(2, 9),
        type: file.type.startsWith('image/') ? 'image' : 'video',
      };

      const error = validateFile(file);
      if (error) {
        fileWithPreview.error = error;
        resolve(fileWithPreview);
        return;
      }

      // Use different preview methods for images vs videos
      if (file.type.startsWith('video/')) {
        // For videos, use object URL instead of data URL
        fileWithPreview.preview = URL.createObjectURL(file);
        resolve(fileWithPreview);
      } else {
        // For images, use data URL as before
        const reader = new FileReader();
        reader.onload = (e) => {
          fileWithPreview.preview = e.target?.result as string;
          resolve(fileWithPreview);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleFiles = useCallback(async (fileList: FileList) => {
    if (disabled) return;

    const newFiles = Array.from(fileList);
    
    // Check if adding these files would exceed the limit
    if (files.length + newFiles.length > maxFiles) {
      alert(`حداکثر ${maxFiles} فایل می‌توانید انتخاب کنید`);
      return;
    }

    // Create previews for new files
    const filesWithPreviews = await Promise.all(
      newFiles.map(file => createFilePreview(file))
    );

    const updatedFiles = [...files, ...filesWithPreviews];
    setFiles(updatedFiles);

    // Only pass valid files to parent
    const validFiles = updatedFiles.filter(f => !f.error);
    onFilesChange(validFiles);
  }, [files, maxFiles, disabled, onFilesChange]);

  const removeFile = useCallback((fileId: string) => {
    // Find the file to remove and clean up its preview URL if it's a video
    const fileToRemove = files.find(f => f.id === fileId);
    if (fileToRemove && fileToRemove.preview && fileToRemove.type === 'video' && fileToRemove.preview.startsWith('blob:')) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    
    const updatedFiles = files.filter(f => f.id !== fileId);
    setFiles(updatedFiles);
    
    const validFiles = updatedFiles.filter(f => !f.error);
    onFilesChange(validFiles);
  }, [files, onFilesChange]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const openFileDialog = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file.preview && file.type === 'video' && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
        
        <div className="space-y-2">
          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              فایل‌های خود را اینجا بکشید یا کلیک کنید
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              حداکثر {maxFiles} فایل، هر فایل حداکثر {maxFileSize} مگابایت
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              فرمت‌های مجاز: JPG, PNG, GIF, WebP, MP4, MOV, AVI, WebM
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              فایل‌های انتخاب شده ({files.length}/{maxFiles})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {files.map((file) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className={`relative group rounded-lg overflow-hidden border-2 ${
                    file.error 
                      ? 'border-red-300 dark:border-red-600' 
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {/* Preview */}
                  <div className="aspect-square bg-gray-100 dark:bg-gray-800">
                    {file.preview && !file.error ? (
                      file.type === 'image' ? (
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={file.preview}
                          className="w-full h-full object-cover"
                          muted
                        />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {file.type === 'image' ? (
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        ) : (
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(file.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                    {file.error && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {file.error}
                      </p>
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.id);
                    }}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* File Type Badge */}
                  <div className={`absolute top-1 left-1 px-1 py-0.5 text-xs rounded ${
                    file.type === 'image' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-blue-500 text-white'
                  }`}>
                    {file.type === 'image' ? 'تصویر' : 'ویدیو'}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiFileUpload;